/**
 * Example Commands Component
 * Shows suggested commands when chat is empty
 */

import React from 'react';
import { 
  MessageSquare, 
  TrendingUp, 
  CreditCard, 
  FileText, 
  Users, 
  Package, 
  FileSearch, 
  Calculator 
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { EXAMPLE_COMMANDS } from '@/types/chat';
import { cn } from '@/lib/utils';

interface ExampleCommandsProps {
  onSelectCommand: (command: string) => void;
}

const commandIcons = {
  "Today's Sales": TrendingUp,
  "Create Payment Voucher": CreditCard,
  "Cash & Bank Balance": MessageSquare,
  "Profit & Loss Report": FileText,
  "Outstanding Receivables": Users,
  "Inventory Status": Package,
  "GST Reports": FileSearch,
  "Trial Balance": Calculator,
};

export const ExampleCommands: React.FC<ExampleCommandsProps> = ({ onSelectCommand }) => {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-semibold mb-2">Talk to Genie</h3>
        <p className="text-muted-foreground">
          Ask me anything about your Tally data or try one of these commands:
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-6xl">
        {EXAMPLE_COMMANDS.map((example) => {
          const Icon = commandIcons[example.title as keyof typeof commandIcons] || MessageSquare;
          
          return (
            <Card
              key={example.title}
              className={cn(
                "p-4 cursor-pointer transition-all duration-200",
                "hover:shadow-md hover:border-primary/50",
                "hover:bg-accent/5"
              )}
              onClick={() => onSelectCommand(example.command)}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium mb-1">{example.title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {example.description}
                  </p>
                  <p className="text-xs text-primary mt-2 font-medium">
                    "{example.command}"
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};